import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/auth")({
  head: () => ({ meta: [{ title: "Admin Login — Texas Made Shade" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: AuthPage,
});

function AuthPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate({ to: "/admin" });
    });
  }, [navigate]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null); setInfo(null);
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email, password,
          options: { emailRedirectTo: `${window.location.origin}/admin` },
        });
        if (error) throw error;
        setInfo("Account created. Check your email to confirm, then sign in. An existing admin must grant you admin access.");
        setMode("signin");
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        navigate({ to: "/admin" });
      }
    } catch (err: any) {
      setError(err.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md border-4 border-navy-deep bg-white p-8 shadow-bold">
        <Link to="/" className="text-xs uppercase tracking-wider text-navy-deep/60 hover:text-red">← Back to site</Link>
        <h1 className="mt-2 font-display text-3xl font-bold uppercase text-navy-deep">
          {mode === "signin" ? "Admin Sign In" : "Create Account"}
        </h1>
        <p className="mt-1 text-sm text-navy-deep/70">Manage customer submissions for texasmadeshade.com</p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label htmlFor="auth-email" className="block text-xs font-bold uppercase tracking-wider text-navy-deep">Email</label>
            <input id="auth-email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full border-2 border-navy-deep px-3 py-2 text-sm" />
          </div>
          <div>
            <label htmlFor="auth-password" className="block text-xs font-bold uppercase tracking-wider text-navy-deep">Password</label>
            <input id="auth-password" type="password" autoComplete="current-password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full border-2 border-navy-deep px-3 py-2 text-sm" />
          </div>
          {error && <p className="text-sm text-red">{error}</p>}
          {info && <p className="text-sm text-navy-deep">{info}</p>}
          <button disabled={busy} type="submit"
            className="w-full bg-red px-4 py-3 font-display text-sm font-bold uppercase tracking-wider text-white hover:bg-navy-deep disabled:opacity-50">
            {busy ? "Please wait…" : mode === "signin" ? "Sign In" : "Create Account"}
          </button>
        </form>

        <button onClick={() => { setMode(mode === "signin" ? "signup" : "signin"); setError(null); setInfo(null); }}
          className="mt-4 w-full text-xs uppercase tracking-wider text-navy-deep/70 hover:text-red">
          {mode === "signin" ? "Need an account? Sign up" : "Already have an account? Sign in"}
        </button>
      </div>
    </div>
  );
}
